import { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslate } from 'react-admin';
import { Dayjs } from 'dayjs';

export type ExportCsvFilter = {
  provider?: string;
  timeFrom?: number;
  timeTo?: number;
};

// Date range filters created_at; from = start-of-day, to = end-of-day (unix ms), matching the
// list's DateInput convention. Null field = no bound; empty provider = ALL providers.
export function buildExportFilter(
  provider: string,
  from: Dayjs | null,
  to: Dayjs | null,
): ExportCsvFilter {
  const filter: ExportCsvFilter = {};
  if (provider) filter.provider = provider;
  if (from) filter.timeFrom = from.startOf('day').valueOf();
  if (to) filter.timeTo = to.endOf('day').valueOf();
  return filter;
}

type Props = {
  open: boolean;
  busy: boolean;
  // i18n namespace, e.g. 'module.xbitRelayDeposits' — supplies .export / .provider / .providerAll
  i18nBase: string;
  providers: string[];
  onClose: () => void;
  onExport: (filter: ExportCsvFilter) => void;
};

export const ExportCsvDialog = ({ open, busy, i18nBase, providers, onClose, onExport }: Props) => {
  const t = useTranslate();
  const [provider, setProvider] = useState('');
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);

  const handleExport = () => {
    if (busy) return;
    onExport(buildExportFilter(provider, fromDate, toDate));
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t(`${i18nBase}.export`)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>{t(`${i18nBase}.provider`)}</InputLabel>
            <Select
              label={t(`${i18nBase}.provider`)}
              value={provider}
              onChange={(e: SelectChangeEvent) => setProvider(e.target.value)}
            >
              <MenuItem value="">{t(`${i18nBase}.providerAll`)}</MenuItem>
              {providers.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DatePicker
            label={t('common.fromTime')}
            value={fromDate}
            onChange={setFromDate}
            format="YYYY/MM/DD"
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
          <DatePicker
            label={t('common.toTime')}
            value={toDate}
            onChange={setToDate}
            format="YYYY/MM/DD"
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">
          {t('ra.action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          {t(`${i18nBase}.export`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
